import { Button, MenuButton } from "@dealer-center/core-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdvancedFiltersSelection from "./AdvancedFiltersSelection";
import { buildFilters } from "../../utils/filters";
import type {
  AdvancedFiltersContainerProps,
  FilterConfigEntry,
  SelectedFilterEntry
} from "@dealer-center/ddc-pages-inventory-archiver-interface";
import {
  AdvancedFilterStyles,
  AdvancedFiltersWrapper
} from "./AdvancedFiltersStyles";

const AdvancedFiltersContainer = (props: AdvancedFiltersContainerProps) => {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterEntry[]>(
    []
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => buildFilters(props.providers),
    [props.providers]
  );

  const handleAddFilter = (filter: FilterConfigEntry) => {
    const exists = selectedFilters.some(f => f.name === filter.name);
    if (!exists) {
      setSelectedFilters([
        ...selectedFilters,
        { name: filter.name, value: "" }
      ]);
    }
  };

  const handleRemoveFilter = (filterName: string) => {
    setSelectedFilters(current =>
      current.filter(item => item.name !== filterName)
    );
    searchParams.delete(filterName);
    setSearchParams(searchParams);
  };

  const handleSaveFilters = () => {
    selectedFilters.forEach(filter => {
      setSearchParams(
        prevParams => {
          prevParams.set(filter.name, filter.value);
          return prevParams;
        },
        { replace: true }
      );
    });
  };

  const handleFilterInputChange = useCallback(
    (filterName: string, value: string) => {
      setSelectedFilters(current =>
        current.map(item =>
          item.name === filterName ? { ...item, value } : item
        )
      );
    },
    []
  );

  useEffect(() => {
    const paramFilters: SelectedFilterEntry[] = Array.from(
      searchParams.entries()
    )
      .filter(([key]) => filters[key])
      .map(([key, value]) => ({
        name: key,
        value
      }));
    setSelectedFilters(paramFilters);
  }, [searchParams, filters]);

  return (
    <AdvancedFiltersWrapper>
      <MenuButton
        ButtonProps={{
          id: "account-selector"
        }}
        MenuProps={{
          id: "account-selector-menu",
          menuOptions: Object.entries(filters).map(([key, filter]) => ({
            onSelect: () => {
              handleAddFilter(filter);
            },
            name: key,
            type: filter.type,
            label: filter.label
          }))
        }}
      >
        Add Filter
      </MenuButton>
      <AdvancedFiltersSelection
        filters={filters}
        handleRemoveFilter={handleRemoveFilter}
        selectedFilters={selectedFilters}
        onChange={handleFilterInputChange}
      />
      {selectedFilters?.length > 0 && (
        <Button
          analyticsConfiguration={{
            eventName: "Archiver Save Filters Clicked"
          }}
          onClick={handleSaveFilters}
        >
          Save
        </Button>
      )}
      <AdvancedFilterStyles />
    </AdvancedFiltersWrapper>
  );
};

export default AdvancedFiltersContainer;
